"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { Suspense } from "react";
import { Home, ArrowLeft, Sparkles } from "lucide-react";
import Navbar from "@/elements/Navbar";
import WhatsAppButton from "@/elements/WhatsAppButton";
import FaceCard from "@/components/ui/face-card";
import { useAppSelector } from "@/store/hooks";

const Footer = dynamic(() => import("@/elements/Footer"), {
  ssr: false,
  loading: () => <div className="h-40 bg-gray-900/5 dark:bg-black/5 animate-pulse" />,
});

export default function NotFound() {
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";

  return (
    <>
      <Navbar />

      <main
        className={`relative overflow-hidden transition-colors duration-700 ${
          isDark ? "bg-[#0B0F19]" : "bg-slate-50"
        }`}
      >
        {/* Background Ambience — same orb + grid pattern used across the site */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className={`absolute top-0 -left-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${
              isDark ? "bg-[#4F11C2]/20 opacity-100" : "bg-[#4F11C2]/10 opacity-50"
            }`}
          />
          <div
            className={`absolute bottom-0 -right-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${
              isDark ? "bg-[#2B00A4]/20 opacity-100" : "bg-[#2B00A4]/10 opacity-50"
            }`}
          />
          <div
            className={`absolute inset-0 transition-opacity duration-700 ${
              isDark ? "opacity-[0.03]" : "opacity-[0.05]"
            }`}
            style={{
              backgroundImage: isDark
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 flex flex-col items-center text-center min-h-[70vh] justify-center">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border backdrop-blur-md transition-all duration-300 ${
              isDark ? "border-[#4F11C2]/30 bg-[#4F11C2]/10" : "border-purple-100 bg-purple-50"
            }`}
          >
            <Sparkles className={`w-4 h-4 ${isDark ? "text-[#8B31FF]" : "text-[#4F11C2]"}`} />
            <span
              className={`font-semibold text-xs uppercase tracking-wider ${
                isDark ? "text-[#8B31FF]" : "text-[#4F11C2]"
              }`}
            >
              Error 404
            </span>
          </div>

          {/* Animated face */}
          <FaceCard color={isDark ? "#8B31FF" : "#4F11C2"} />

          <h1
            className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 tracking-tight leading-[1.15] ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Page{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4F11C2] to-[#8B31FF]">
              Not Found
            </span>
          </h1>

          <p
            className={`text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            The page you&apos;re looking for doesn&apos;t exist or may have been moved. Let&apos;s get
            you back on track.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              href="/"
              className="group cursor-pointer px-8 py-4 bg-gradient-to-r from-[#4F11C2] to-[#8B31FF] text-white rounded-lg font-semibold text-lg shadow-2xl flex items-center gap-2 transition-transform duration-300 hover:scale-105"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Link>

            <Link
              href="/contact"
              className={`px-8 py-4 rounded-lg font-semibold text-lg border flex items-center gap-2 transition-all duration-300 ${
                isDark
                  ? "border-white/15 text-white hover:bg-white/10"
                  : "border-gray-200 text-gray-800 hover:bg-gray-100"
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              Contact Us
            </Link>
          </div>
        </div>
      </main>

      <Suspense fallback={<div className="h-20" />}>
        <Footer />
      </Suspense>
      <WhatsAppButton />
    </>
  );
}
